import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Elements, ExpressCheckoutElement, PaymentElement, useElements, useStripe,
} from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import type { Stripe } from '@stripe/stripe-js';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { ApiRequestError } from '../api/client';
import { api } from '../api/shop';
import { formatMoney } from '../brand/brand';
import type { Deal, OrderCreated } from '../api/types';

/**
 * Checkout.
 *
 * The card fields are Stripe's, rendered in an iframe inside our page, so the card
 * number never reaches our JavaScript or our server. What we own is the surrounding
 * flow — and that is where the interesting failures live:
 *
 *   The order and the payment intent are created ONCE, before the form renders, with
 *   an idempotency key held in a ref. React 18 StrictMode double-invokes effects in
 *   development; without the guard that is two orders for one customer.
 *
 *   `redirect: 'if_required'` keeps the buyer on our page unless their bank insists
 *   on a redirect. When it does, `return_url` brings them back to the order page,
 *   which reconciles with the provider — so the flow completes either way.
 *
 *   After a successful confirm we do NOT claim the voucher exists. The webhook may
 *   not have arrived yet. The order page polls and reconciles; saying "done" here
 *   would be asserting something we have not checked.
 */
export function Checkout() {
  const { dealId } = useParams<{ dealId: string }>();
  const [params] = useSearchParams();

  /*
   * Quantity comes from the URL, and is SANITISED here rather than trusted.
   *
   * It arrives from the deal page as ?qty=3, which means it also arrives from a
   * bookmark, a shared link, and anyone who fancies typing ?qty=-1 or ?qty=1e9.
   * Clamped to 1..10 so a nonsense value becomes a sensible order rather than an
   * error page. The server independently enforces maxPerUser and stock — this
   * clamp is about not sending it obvious rubbish, not about safety.
   */
  const quantity = (() => {
    const raw = Number.parseInt(params.get('qty') ?? '1', 10);
    return Number.isFinite(raw) ? Math.min(Math.max(raw, 1), 10) : 1;
  })();

  const [deal, setDeal] = useState<Deal | null>(null);
  const [order, setOrder] = useState<OrderCreated | null>(null);
  const [error, setError] = useState<string | null>(null);

  /*
   * WHY THIS EFFECT IS SHAPED THE WAY IT IS.
   *
   * It must do two things that pull against each other: create EXACTLY ONE order
   * per deal, and always finish — and the obvious way to write it does neither.
   *
   * The previous version combined a `started` ref with a per-run `cancelled`
   * flag. Under React 18 StrictMode, which double-invokes effects in
   * development, that produces a component that can never load:
   *
   *   1. effect runs      -> started = true, request goes out, cancelled = false
   *   2. cleanup runs     -> that run's cancelled = true
   *   3. effect runs again-> started is already true, so it returns immediately
   *   4. the request from step 1 resolves -> `if (!cancelled)` is false, so the
   *                          state is never set
   *
   * Nothing errors. The order really is created on the server. The page just sits
   * on "Preparing checkout…" for ever, which is why the API logs looked innocent.
   *
   * So: the guard is keyed by dealId and holds the in-flight promise, and there
   * is no `cancelled` flag. Setting state on an unmounted component is a no-op in
   * React 18 and does not warn, so guarding against it bought nothing and cost
   * this.
   */
  const attempt = useRef<{ dealId: string; quantity: number; key: string } | null>(null);

  useEffect(() => {
    if (dealId === undefined) return;

    // Keyed by dealId, not a bare boolean. Navigating from /checkout/A to
    // /checkout/B reuses this component rather than remounting it, so a boolean
    // would leave the page showing deal A's order for deal B.
    if (attempt.current?.dealId === dealId && attempt.current.quantity === quantity) return;

    // A NEW idempotency key per deal. Reusing one across deals would make the
    // server replay the FIRST order — correctly, by its own rules — and the buyer
    // would be charged for something they did not choose. The key is stored
    // alongside the deal id precisely so the two can never drift apart.
    attempt.current = { dealId, quantity, key: crypto.randomUUID() };
    const key = attempt.current.key;

    setDeal(null);
    setOrder(null);
    setError(null);

    async function begin(id: string) {
      try {
        const found = await api.deal(id);

        // Still the attempt we started? A slow response for a deal the buyer has
        // navigated away from must not overwrite the current one. Compared by
        // key, so this is about staleness rather than mounting.
        if (attempt.current?.key !== key) return;
        setDeal(found);

        const created = await api.createOrder(id, quantity, key);
        if (attempt.current?.key !== key) return;
        setOrder(created);
      } catch (ex) {
        if (attempt.current?.key !== key) return;
        setError(
          ex instanceof ApiRequestError
            ? ex.message
            : 'We could not start checkout. Please try again.',
        );
      }
    }

    void begin(dealId);
  }, [dealId, quantity]);

  // loadStripe returns a promise that must be created once, not per render.
  const stripePromise = useMemo<Promise<Stripe | null> | null>(
    () => (order === null ? null : loadStripe(order.publishableKeyHint)),
    [order],
  );

  if (error !== null) {
    return (
      <div className="panel narrow" style={{ marginTop: 40 }}>
        <h2>Checkout unavailable</h2>
        <p className="muted" style={{ marginTop: 8 }}>{error}</p>
        <p className="faint" style={{ marginTop: 12 }}>Nothing has been charged.</p>
      </div>
    );
  }

  if (deal === null || order === null || stripePromise === null) {
    return <p className="muted" style={{ padding: '48px 0' }}>Preparing checkout…</p>;
  }

  return (
    <div className="stack narrow gap-lg" style={{ marginBlock: 32 }}>
      <div className="panel stack" style={{ gap: 6 }}>
        <p className="faint">{deal.businessName}</p>
        <h2>{deal.title}</h2>

        {/* The order's amount, not the deal's unit price. They differ whenever
            quantity > 1, and the figure shown beside a card form has to be the
            one that will actually be charged. */}
        <div className="price-row">
          <span className="price">{formatMoney(order.amountCents)}</span>
          {quantity > 1 && (
            <span className="get">
              {quantity} × {formatMoney(deal.priceCents)}
            </span>
          )}
          <span className="badge end">
            Save {formatMoney(deal.savingsCents * quantity)}
          </span>
        </div>

        <p className="faint" style={{ margin: 0 }}>
          {quantity > 1
            ? `${quantity} vouchers, ${formatMoney(deal.faceValueCents * quantity)} of credit in total`
            : `${formatMoney(deal.faceValueCents)} of credit`}
        </p>
      </div>

      <Elements
        stripe={stripePromise}
        options={{
          clientSecret: order.clientSecret,
          appearance: { theme: 'stripe' },
        }}
      >
        <PaymentForm orderId={order.orderId} amountCents={order.amountCents} />
      </Elements>
    </div>
  );
}

function PaymentForm({ orderId, amountCents }: { orderId: string; amountCents: number }) {
  const stripe = useStripe();
  const elements = useElements();
  const navigate = useNavigate();

  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  // Whether a wallet button actually rendered. Stripe tells us on ready; there is
  // no way to know beforehand, because it depends on the device, the browser and
  // whether the customer has a card in their wallet at all.
  const [expressReady, setExpressReady] = useState(false);

  /**
   * ONE confirm path for both the card form and the wallet buttons.
   *
   * `stripe.confirmPayment({ elements })` reads whichever element the customer
   * actually used, so Apple Pay and a typed card converge here — and the
   * post-payment handling, which is the part with the reasoning in it, cannot
   * drift between the two.
   */
  const confirm = useCallback(
    async () => {
      // Not ready yet — the iframe is still mounting. Guarding here is why a fast
      // double-click cannot fire a confirm against a half-initialised element.
      if (stripe === null || elements === null || busy) return;

      setBusy(true);
      setMessage(null);

      const { error, paymentIntent } = await stripe.confirmPayment({
        elements,
        confirmParams: {
          // Used only when the bank forces a full redirect. It returns to the order
          // page, which reconciles with the provider — so the flow finishes even
          // when we never see the confirm result ourselves.
          return_url: `${window.location.origin}/orders/${orderId}`,
        },
        redirect: 'if_required',
      });

      if (error) {
        // card_error and validation_error are the customer's to act on and safe to
        // show. Anything else is ours, and its message can name internals — so it
        // gets a generic line instead.
        setMessage(
          error.type === 'card_error' || error.type === 'validation_error'
            ? error.message ?? 'That payment did not go through.'
            : 'Something went wrong taking payment. You have not been charged twice — please check your order.',
        );

        setBusy(false);
        return;
      }

      // Deliberately NOT "your voucher is ready". The money is captured; the voucher
      // is issued by the webhook, which may not have landed. The order page finds
      // out for certain rather than us guessing.
      if (paymentIntent?.status === 'succeeded' || paymentIntent?.status === 'processing') {
        navigate(`/orders/${orderId}`, { replace: true });
        return;
      }

      setMessage('That payment was not completed. Please try again.');
      setBusy(false);
    },
    [stripe, elements, busy, orderId, navigate],
  );

  const submit = useCallback(
    (event: React.FormEvent) => {
      event.preventDefault();
      void confirm();
    },
    [confirm],
  );

  return (
    <form className="panel stack" style={{ gap: 18 }} onSubmit={submit}>
      {/*
        APPLE PAY AND GOOGLE PAY. SOW §2.2 names both.

        A SEPARATE ELEMENT ABOVE THE CARD FORM, not a row inside it. The wallet
        buttons are the fastest path for the people who have them — one touch,
        no typing, address and email already filled — and burying them in an
        accordion below "Card" means most of those people type a card number
        instead.

        IT RENDERS NOTHING when neither wallet is available: no configured
        wallet, no HTTPS, an unsupported browser, or a device with no card in
        its wallet. That is why `expressReady` gates the divider too — a rule
        labelled "or pay by card" above an empty space is worse than no divider.

        Two things outside this file have to be true or these never appear, and
        both are one-off admin tasks rather than code:
          1. Apple Pay and Google Pay enabled in the Stripe dashboard.
          2. The live domain registered with Apple, via Stripe's payment method
             domains. localhost is exempt; a preview domain is not.
        docs/naya-face-wallet-pay-setup.md has the steps.
      */}
      <ExpressCheckoutElement
        options={{ buttonHeight: 48 }}
        onReady={({ availablePaymentMethods }) => {
          setExpressReady(availablePaymentMethods !== undefined
            && Object.keys(availablePaymentMethods).length > 0);
        }}
        onConfirm={() => void confirm()}
      />

      {expressReady && (
        <p className="or-divider"><span>or pay by card</span></p>
      )}

      <PaymentElement />

      {message !== null && <p className="notice error">{message}</p>}

      <button type="submit" className="btn block" disabled={stripe === null || busy}>
        {busy ? 'Processing…' : `Pay ${formatMoney(amountCents)}`}
      </button>

      <p className="faint center">
        Card details go straight to our payment provider. They never touch our servers.
      </p>
    </form>
  );
}
